import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, Alert, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fmt, fmtCap } from './constants';

const POSITIVE_COLOR = '#059669';
const NEGATIVE_COLOR = '#dc2626';
const NEUTRAL_COLOR = '#334155';

function getDiffMetrics(stock, average, lowerIsBetter) {
  if (stock == null || average == null) {
    return { diff: null, isBetter: null, color: NEUTRAL_COLOR };
  }

  const diff = ((stock - average) / average) * 100;
  const isBetter = lowerIsBetter ? diff < 0 : diff > 0;

  return {
    diff,
    isBetter,
    color: isBetter ? POSITIVE_COLOR : NEGATIVE_COLOR,
  };
}

function buildAverageMetrics(t, target, averages) {
  return [
    { label: t('icb.peers.metrics.pe'), stock: target?.pe, avg: averages.avg_pe, lower: true },
    { label: t('icb.peers.metrics.pb'), stock: target?.pb, avg: averages.avg_pb, lower: true },
    { label: t('icb.peers.metrics.roe'), stock: target?.roe, avg: averages.avg_roe, lower: false },
    { label: t('icb.peers.metrics.marketCap'), stock: target?.market_cap_bn, avg: averages.avg_market_cap_bn, isCap: true, lower: false },
  ];
}

function renderMetricValue(metric) {
  return metric.isCap ? fmtCap(metric.stock) : fmt(metric.stock, 2);
}

function renderAverageValue(metric) {
  return metric.isCap ? fmtCap(metric.avg) : fmt(metric.avg, 2);
}

function getPeerMetricColor(value, average, lowerIsBetter) {
  if (value == null || average == null) {
    return undefined;
  }

  if (lowerIsBetter) {
    return value <= average ? POSITIVE_COLOR : NEGATIVE_COLOR;
  }

  return value >= average ? POSITIVE_COLOR : NEGATIVE_COLOR;
}

function formatRoeValue(value) {
  if (value == null) {
    return '-';
  }

  return `${fmt(value, 1)}%`;
}

const PeersTab = ({ peerTicker, setPeerTicker, peerData, peerLoading, fetchPeers, tickerOptions }) => {
  const { t } = useTranslation();

  return (
    <Box>
      {/* Input bar */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box
            component="input"
            value={peerTicker}
            onChange={(e) => setPeerTicker(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchPeers(peerTicker); }}
            placeholder="VNM, FPT..."
            sx={{
              px: 1.5, py: 0.75, border: '1px solid #e2e8f0', borderRadius: 1.5,
              fontSize: '0.85rem', width: 160, outline: 'none', fontFamily: 'inherit',
              '&:focus': { borderColor: '#3b82f6' },
            }}
          />
          <Box
            component="button"
            onClick={() => fetchPeers(peerTicker)}
            sx={{ px: 2.5, py: 0.9, border: 'none', borderRadius: 1.5, bgcolor: '#3b82f6', color: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', '&:hover': { bgcolor: '#2563eb' }, transition: 'background 0.2s' }}
          >
            {t('icb.peers.compareBtn')}
          </Box>

          {tickerOptions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', ml: 0.5, alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 600, mr: 0.25 }}>{t('icb.peers.suggestions')}:</Typography>
              {tickerOptions.slice(0, 10).map((tk) => (
                <Typography
                  key={tk}
                  component="span"
                  onClick={() => { setPeerTicker(tk); fetchPeers(tk); }}
                  sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#3b82f6', cursor: 'pointer', px: 0.5, '&:hover': { textDecoration: 'underline' } }}
                >
                  {tk}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {peerLoading && <LinearProgress sx={{ mb: 2 }} />}

      {peerData && (() => {
        const target = peerData.peers?.find(p => p.is_target);
        const avg = peerData.peer_averages || {};
        const averageMetrics = buildAverageMetrics(t, target, avg);
        return (
          <>
            {/* Target stock header + averages comparison */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>{peerData.ticker}</Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>{peerData.company_name}</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{peerData.icb_name_vi || peerData.icb_name_en}</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>{peerData.icb_name_en} — {peerData.peer_count} {t('icb.common.stocks')}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}>
                {averageMetrics.map((m) => {
                  const { diff, isBetter, color } = getDiffMetrics(m.stock, m.avg, m.lower);
                  return (
                    <Box key={m.label} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', mb: 0.5 }}>{m.label}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                        <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color, fontFeatureSettings: '"tnum"', lineHeight: 1 }}>
                          {renderMetricValue(m)}
                        </Typography>
                        {diff != null && (
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: isBetter ? POSITIVE_COLOR : NEGATIVE_COLOR }}>
                            {diff >= 0 ? '+' : ''}{diff.toFixed(0)}%
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', mt: 0.25 }}>
                        {t('icb.common.industryAvg')}: {renderAverageValue(m)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            {/* Peers table */}
            <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                  {t('icb.peers.samePeersTitle')} ({peerData.peers?.length || 0})
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                  {t('icb.peers.sortHint')}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        { k: 'ticker', a: 'left' }, { k: 'company', a: 'left' }, { k: 'exchange', a: 'left' },
                        { k: 'marketCap', a: 'right' }, { k: 'pe', a: 'right' }, { k: 'pb', a: 'right' }, { k: 'roe', a: 'right' },
                      ].map(({ k, a }) => (
                        <TableCell key={k} align={a}
                          sx={{ fontWeight: 700, bgcolor: '#f8f9fa', fontSize: '0.75rem', py: 1 }}>
                          {t(`icb.peers.headers.${k}`)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {peerData.peers?.map((p) => {
                      const peColor = getPeerMetricColor(p.pe, avg.avg_pe, true);
                      const pbColor = getPeerMetricColor(p.pb, avg.avg_pb, true);
                      const roeColor = getPeerMetricColor(p.roe, avg.avg_roe, false);
                      return (
                        <TableRow key={p.ticker} hover sx={{ bgcolor: p.is_target ? '#eff6ff' : 'inherit', '&:nth-of-type(even):not(:hover)': { bgcolor: p.is_target ? '#eff6ff' : '#fafafa' } }}>
                          <TableCell>
                            <Typography fontWeight={p.is_target ? 800 : 600} fontSize="0.85rem" color={p.is_target ? '#3b82f6' : 'inherit'}>{p.ticker}</Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{p.company_name}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', color: '#64748b' }}>{p.exchange}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, fontFeatureSettings: '"tnum"' }}>{fmtCap(p.market_cap_bn)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: p.is_target ? 700 : 500, color: peColor, fontFeatureSettings: '"tnum"' }}>{fmt(p.pe, 1)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: p.is_target ? 700 : 500, color: pbColor, fontFeatureSettings: '"tnum"' }}>{fmt(p.pb, 2)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: p.is_target ? 700 : 500, color: roeColor, fontFeatureSettings: '"tnum"' }}>{formatRoeValue(p.roe)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        );
      })()}

      {!peerData && !peerLoading && (
        <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
          {t('icb.peers.noSelection')}
        </Alert>
      )}
    </Box>
  );
};

PeersTab.propTypes = {
  peerTicker: PropTypes.string.isRequired,
  setPeerTicker: PropTypes.func.isRequired,
  peerData: PropTypes.shape({
    ticker: PropTypes.string,
    company_name: PropTypes.string,
    icb_name_vi: PropTypes.string,
    icb_name_en: PropTypes.string,
    peer_count: PropTypes.number,
    peer_averages: PropTypes.shape({
      avg_pe: PropTypes.number,
      avg_pb: PropTypes.number,
      avg_roe: PropTypes.number,
      avg_market_cap_bn: PropTypes.number,
    }),
    peers: PropTypes.arrayOf(PropTypes.shape({
      ticker: PropTypes.string,
      company_name: PropTypes.string,
      exchange: PropTypes.string,
      market_cap_bn: PropTypes.number,
      pe: PropTypes.number,
      pb: PropTypes.number,
      roe: PropTypes.number,
      is_target: PropTypes.bool,
    })),
  }),
  peerLoading: PropTypes.bool.isRequired,
  fetchPeers: PropTypes.func.isRequired,
  tickerOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default PeersTab;
